import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { generate_otp, verify_otp } from "../../api/agent";
import { usePlaygroundContext } from "../Playground";

interface OTPPopupProps {
  open: boolean;
  onClose: () => void;
  email: any;
  onNext: () => void;
}

const OTPPopup: React.FC<OTPPopupProps> = ({
  open,
  onClose,
  email,
  onNext,
}) => {
  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const inputRefs = useRef<(HTMLInputElement | null)[]>(
    new Array(6).fill(null)
  );
  const [apiError, setApiError] = useState<string | null>(null);
  const [timer, setTimer] = useState<number>(60);

  useEffect(() => {
    if (open) {
      setTimer(60); // Reset timer when dialog opens
    }
  }, [open]);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const handleResend = () => {
    let payload = {
      companyName: playground.companyName,
      fullName: playground.fullName,
      companyEmail: playground.companyEmail,
      phoneNumber: playground.phoneNumber,
    };
    generate_otp(payload).then((response) => {
      setTimer(60);
    });
  };

  const validationSchema = Yup.object({
    otp: Yup.string()
      .length(6, "Verification code must be 6 digits")
      .matches(/^[A-Za-z0-9]+$/, "Only letters and numbers allowed")
      .required("Verification code is required"),
  });
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <Paper
        elevation={5}
        sx={{ backgroundColor: "#FFF", borderRadius: "8px" }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            fontSize: "24px",
            lineHeight: "30px",
            letterSpacing: "0%",
            textAlign: "center",
          }}
          mt={1}
        >
          Verify Your Email Address
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "16.8px",
              letterSpacing: "0%",
              textAlign: "center",
            }}
            variant="body1"
            gutterBottom
          >
            A verification code has been sent to your registered email:
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "16.8px",
              letterSpacing: "0%",
              textAlign: "center",
            }}
            variant="body1"
            gutterBottom
          >
            {email}
          </Typography>

          <Formik
            initialValues={{ otp: "" }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setApiError(null);
              verify_otp({ companyEmail: email, otp: values.otp })
                .then((response) => {
                  setSubmitting(false);
                  onNext();
                })
                .catch(() => {
                  setSubmitting(false);
                  console.log("Verification failed");
                  setApiError(
                    "Invalid or Expired verification code. Please try again."
                  );
                });
            }}
          >
            {({ values, errors, touched, setFieldValue, handleSubmit }) => {
              const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
                
                const pasteData = event.clipboardData.getData("text").toUpperCase();
                if (!/^[A-Za-z0-9]{6}$/.test(pasteData)) return; // Allow only 6 alphanumeric characters
              
                setFieldValue("otp", pasteData);
                pasteData.split("").forEach((char, i) => {
                  if (inputRefs.current[i]) {
                    inputRefs.current[i]!.value = char;
                  }
                });
                inputRefs.current[5]?.focus();
              };
              
              const handleChange = (
                index: number,
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const inputElement = event.target as HTMLInputElement;
                const value = inputElement.value.toUpperCase(); // Convert to uppercase

                if (!/^[A-Za-z0-9]?$/.test(value)) return; // Allow only alphanumeric characters
              
                let newOtp = values.otp.split("");
                newOtp[index] = value;
                const otpString = newOtp.join("");
              
                setFieldValue("otp", otpString);
              
                if (value && index < 5) {
                  inputRefs.current[index + 1]?.focus();
                }
                setApiError(null);
              };

              const handleKeyDown = (
                index: number,
                event: React.KeyboardEvent<HTMLDivElement>
              ) => {
                if (
                  event.key === "Backspace" &&
                  !values.otp[index] &&
                  index > 0
                ) {
                  inputRefs.current[index - 1]?.focus();
                }
              };

              return (
                <Form onSubmit={handleSubmit}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    gap={{ xs: 1, md: 2 }}
                    mt={3}
                  >
                    {[...Array(6)].map((_, index) => (
                      <Field
                        key={index}
                        name="otp"
                        render={({ field }: any) => (
                          <TextField
                            {...field}
                            value={values.otp[index] || ""}
                            onChange={(e) => handleChange(index, e)}
                            onPaste={handlePaste}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            inputRef={(el) => (inputRefs.current[index] = el)}
                            variant="outlined"
                            error={Boolean(errors.otp) || Boolean(apiError)}
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: "center",
                                padding: "8px",
                                fontSize: "16px",
                                ...(window.innerWidth < 600 && {
                                  padding: "4px",
                                  fontSize: "14px",
                                }), // Adjust for mobile
                              },
                            }}
                            sx={{
                              width: 50,
                              backgroundColor: "#F7F7F7",
                              borderRadius: "4px",
                              height: { xs: "40px", sm: "55px" },
                              "& .MuiInputBase-root": {
                                height: "100%", // Ensures inner input takes full height
                              },
                            }}
                          />
                        )}
                      />
                    ))}
                  </Box>

                  <FormHelperText error sx={{ textAlign: "center", mt: 1 }}>
                    {touched.otp && errors.otp}
                  </FormHelperText>
                  <FormHelperText error sx={{ textAlign: "center", mt: 1 }}>
                    {apiError}
                  </FormHelperText>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "16.8px",
                      letterSpacing: "0%",
                      textAlign: "center",
                      paddingLeft: { xs: "0px", sm: "47px" },
                      paddingRight: { xs: "0px", sm: "45px" },
                    }}
                    variant="body2"
                    mt={2}
                  >
                    If this email ID is incorrect or you wish to use a different
                    one, you can update it by clicking{" "}
                    <Typography
                      onClick={onClose}
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: "#0066F4",
                        cursor: "pointer",
                      }}
                    >
                      "Change here"
                    </Typography>
                    .
                  </Typography>

                  <Box display="flex" justifyContent="center" mt={2}>
                    {timer > 0 ? (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "16.8px",
                          letterSpacing: "0%",
                          textAlign: "center",
                        }}
                        variant="body2"
                      >
                        Resend code in {timer} seconds
                      </Typography>
                    ) : (
                      <Typography
                        onClick={handleResend}
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: "#0066F4",
                          cursor: "pointer",
                        }}
                      >
                        Resend OTP
                      </Typography>
                    )}
                  </Box>

                  <Box display="flex" justifyContent="center" mt={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        borderRadius: "60px",
                        backgroundColor: "#0066F4",
                        color: "white",
                        textTransform: "none",
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Paper>
    </Dialog>
  );
};

export default OTPPopup;
